import React, { FC, useMemo, useRef, useState } from 'react';
import { Divider, Flex, HStack, Stack, Text, VStack } from '@chakra-ui/react';
import { IListingInBuyPage, Props } from './HouseDetailContainerProps';
import {
  areaFormatter,
  chineseNumeralFormatter,
  dollarLabelFormatter,
  itemHasOrNotFormatter,
  lotSizeTitleLangKeyFormatter,
  unitAreaLabelFormatter,
} from '@/src/libs/number-helper';
import ItemFeature from '@/src/components/UI/HouseDetail/ItemFeature';
import MapViewSection from '@/src/components/UI/HouseDetail/MapViewSection';
import HomeLoanCalculation from '@/src/components/UI/HouseDetail/HomeLoanCalculation';
import ContentContainer from '@/src/components/UI/ContentContainer';
import Link from 'next/link';
import { ContactFooter } from './ContactFooter';
import FullPage from '@/src/components/UI/FullPage';
import dynamic from 'next/dynamic';
import ConditionalFragment from '@/components/common/ConditionalFragment';
import { isEmptyString, isNotSet, isSet } from '@/utils/formatChecker';
import classNames from 'classnames';
import useT from '@/lib//t';
import {
  getBuildingCompleteDate,
  getFloorInformation,
  getParkingDetail,
  totalSizeFormatter,
} from '@/utils/dataConvertor';
import { amenitiesFormatter } from '@/src/libs/special-helper';
import Icon from '@/public/icon';
import Gallery, { GallerySection } from '@/src/components/feature/Gallery';
import { BuildingShowcaseDetailsEntity, CountryEnum } from '@/swagger';
import AgentSection from '@/src/components/UI/HouseDetail/AgentSection';
import { getContactCTAText, getContactFormPath } from '@/pages/building/[...entity]';
import { getLayout } from '@/src/helper/listing';

const SubNavBar = dynamic(() => import('@/src/components/UI/HouseDetail/SubNavBar'), {
  ssr: false,
});

const getDisplayValueAndUnit = ({ formatter, key, unitFormatter }: MainData, detailData: IListingInBuyPage) => {
  let displayValue: string | number | null = null;
  let unit = '';

  if (formatter) {
    displayValue = formatter(detailData);
  } else if (key in detailData) {
    const data = detailData[key as keyof IListingInBuyPage];
    // noinspection SuspiciousTypeOfGuard
    if (typeof data === 'string') {
      displayValue = data;
    } else if (data) {
      displayValue = data.toString();
    }
  }

  if (unitFormatter) {
    unit = unitFormatter(detailData['country']!);
  }

  return { displayValue, unit };
};

interface MainData {
  key: keyof IListingInBuyPage | string;
  title: string;
  isSubItem?: boolean;
  noLeftPadding?: boolean;
  endWithBorder?: boolean;
  formatter?: (listing: IListingInBuyPage) => string | number | null;
  unitFormatter?: (country: string) => string;
  hidden?: boolean;
  collapseData?: Array<MainData>;
}

const HouseDetailContainer: FC<Props> = ({ detailData, building }) => {
  const t = useT('listing');

  const mainTitle = useRef<HTMLDivElement>(null);
  const videoTitle = useRef<HTMLDivElement>(null);
  const featureTitle = useRef<HTMLDivElement>(null);
  const environmentTitle = useRef<HTMLDivElement>(null);

  const parkingSize = Number.isNaN(parseFloat(detailData.detail_parking_size ?? ''))
    ? undefined
    : detailData.detail_parking_size;

  const trafficEnv = useMemo(
    () => ({
      school: detailData.traffic_env_school,
      recreation: detailData.traffic_env_recreation,
      hospital: detailData.traffic_env_hospital,
      supermarket: detailData.traffic_env_supermarket,
      traffic: detailData.traffic_env_traffic,
    }),
    [
      detailData.traffic_env_school,
      detailData.traffic_env_recreation,
      detailData.traffic_env_hospital,
      detailData.traffic_env_supermarket,
      detailData.traffic_env_traffic,
    ]
  );
  const { price, dealPrice, display_address, building_project, display_building_project, title, country, sid } =
    detailData;

  const parkingDetail = useMemo(() => getParkingDetail(detailData).join('、'), [detailData]);

  const mainData = useMemo<Array<MainData>>(
    () =>
      (
        [
          {
            key: 'listing_advanced_info',
            title: '建設公司',
            formatter: (data) => data.listing_advanced_info?.builder_project_name,
            hidden:
              !detailData.listing_advanced_info?.builder_project_name ||
              !detailData.listing_advanced_info.display_builder_project_name,
          },
          { key: 'detail_category', title: '建案類型', hidden: !detailData.detail_category },
          { key: 'detail_purpose', title: '登記用途', hidden: !detailData.detail_purpose },
          { key: 'detail_floor', title: '樓層', formatter: getFloorInformation },
          {
            key: 'detail_age',
            title: '完工時間',
            formatter: ({ year_of_building_completion, month_of_building_completion }) =>
              getBuildingCompleteDate(year_of_building_completion, month_of_building_completion),
          },
          {
            key: 'detail_total_area_size',
            title: '總面積',
            formatter: () => totalSizeFormatter(detailData.detail_total_area_size, detailData.country!),
          },
          {
            key: 'house_detail_total_area_size',
            title: '房屋權狀坪數',
            formatter: (data) =>
              areaFormatter(data['detail_total_area_size'], {
                omit: [parkingSize!],
                country: data.country!,
              }),
            unitFormatter: unitAreaLabelFormatter,
            hidden:
              isNotSet(detailData.detail_main_area_size) &&
              isNotSet(detailData.detail_other_area_size) &&
              isNotSet(detailData.detail_amenities_area) &&
              isNotSet(parkingSize),
            collapseData: [],
          },
          {
            key: 'detail_main_area_size',
            title: '主建物',
            isSubItem: true,
            noLeftPadding: true,
            formatter: (data) => areaFormatter(data['detail_main_area_size'], { country: data.country! }),
            unitFormatter: unitAreaLabelFormatter,
            hidden: isNotSet(detailData['detail_main_area_size']),
          },
          {
            key: 'detail_other_area_size',
            title: '附屬建物',
            isSubItem: true,
            noLeftPadding: true,
            formatter: (data) => areaFormatter(data['detail_other_area_size'], { country: data.country! }),
            unitFormatter: unitAreaLabelFormatter,
            hidden: isNotSet(detailData['detail_other_area_size']),
          },
          {
            key: 'detail_amenities_area',
            title: `公共設施${amenitiesFormatter(detailData.is_parking_space_in_amenities)}`,
            isSubItem: true,
            noLeftPadding: true,
            formatter: (data) => areaFormatter(data['detail_amenities_area'], { country: data.country! }),
            unitFormatter: unitAreaLabelFormatter,
            endWithBorder: isSet(parkingSize) && !detailData.is_parking_space_in_amenities,
            hidden: isNotSet(detailData['detail_amenities_area']),
          },
          {
            key: 'detail_parking_size',
            title: `車位`,
            isSubItem: true,
            noLeftPadding: true,
            formatter: (data) => areaFormatter(parkingSize!, { country: data.country! }),
            unitFormatter: unitAreaLabelFormatter,
            hidden: !(parkingSize && !detailData.is_parking_space_in_amenities),
          },
          {
            key: 'detail_lot_size',
            title: t(lotSizeTitleLangKeyFormatter(detailData.detail_category)),
            formatter: (data) => areaFormatter(data['detail_lot_size'], { country: data.country! }),
            unitFormatter: unitAreaLabelFormatter,
          },
          {
            key: 'detail_guard_management',
            title: '警衛管理',
            formatter: (data) => itemHasOrNotFormatter(data['detail_guard_management']),
          },
          { key: 'detail_management_fee', title: '管理費' },
          {
            key: 'parking_detail_row',
            title: '車位類型',
            formatter: () => parkingDetail,
            hidden: isEmptyString(parkingDetail),
          },
          { key: 'detail_amenities', title: '公共設施' },
          { key: 'detail_price_per_ping', title: '每坪單價' },
          {
            key: 'public_usage_ratio',
            title: '公設比',
            formatter: (data) => `${data.listing_advanced_info?.public_usage_ratio}%`,
            hidden: !detailData.listing_advanced_info?.public_usage_ratio || country !== CountryEnum.TW,
          },
        ] as Array<MainData>
      ).filter(isSet),
    [detailData]
  );

  const imageSections = useMemo(() => {
    return detailData.listing_image.reduce(
      (acc, cur) => {
        if (cur.tag === 'layout') {
          acc[1].images.push(cur.watermark_image ?? cur.original_image ?? '');
        } else {
          acc[0].images.push(cur.watermark_image ?? cur.original_image ?? '');
        }
        return acc;
      },
      [
        { key: 'default', text: undefined, images: [] },
        { key: 'layout', text: '格局圖', images: [], icon: <Icon.FloorPlanIcon /> },
      ] as Array<GallerySection>
    );
  }, [detailData.listing_image]);

  const videos = useMemo(
    () => detailData.listing_video.map((video) => video.video_url ?? ''),
    [detailData.listing_video]
  );

  return (
    <FullPage
      hasPadding={false}
      sid={detailData?.sid}
      headerRight={['favorite', 'share']}
      title={''}
      appContentProps={{
        pt: `60px`,
      }}
      hasBottomMargin={false}
    >
      <div className={'sm:mb-4'}>
        <Gallery imageSections={imageSections} videos={videos} />
      </div>
      <Caution className={'sm:block hidden sm:visible invisible'} />
      <Flex direction={'column'} w={'full'} px={4} pt={2}>
        <HStack w={'100%'} justify={'space-between'}>
          <Text lineHeight={6} color={'black'} fontWeight={700} flex={1}>
            {chineseNumeralFormatter((dealPrice ? dealPrice : price)!, country!)}
            {dollarLabelFormatter(detailData.country!)}
          </Text>
          <Text lineHeight={6} color={'black'} fontWeight={700} flex={1} textAlign={'center'}>
            {getLayout(detailData, detailData.floor_plan_type)}
          </Text>
          <Text lineHeight={6} color={'black'} fontWeight={700} flex={1} textAlign={'right'}>
            {detailData.detail_total_area_size ? detailData.detail_total_area_size : '-'}{' '}
            {unitAreaLabelFormatter(country!)}
          </Text>
        </HStack>
        <HStack w={'100%'} margin={0} justify={'space-between'}>
          <Text fontSize={12} color={'black'} flex={1} textAlign={'left'}>
            {dealPrice ? '成交價' : '房價'}
          </Text>
          <Text fontSize={12} color={'black'} flex={1} textAlign={'center'}>
            格局
          </Text>
          <Text fontSize={12} color={'black'} flex={1} textAlign={'right'}>
            總面積
          </Text>
        </HStack>
        <VStack pt={3} align={'flex-start'} w={'100%'}>
          <HStack align={'center'} justify={'space-between'}>
            <Text lineHeight={5} fontSize={16} color={'gray.700'}>
              {title}
            </Text>
            {display_building_project && building_project && (
              <>
                <Divider className='w-px bg-gray700 mx-3' style={{ height: '1rem' }} orientation='vertical' />
                <Text mt={-1} lineHeight={5} fontSize={16} color={'gray.700'}>
                  {display_building_project && building_project ? building_project : ''}
                </Text>
              </>
            )}
          </HStack>
          <HStack mt={-1} alignItems={'center'} justifyContent={'space-between'} w={'full'}>
            <Text lineHeight={5} fontSize={16} color={'gray.700'}>
              {display_address}
            </Text>
            <Text lineHeight={5} fontSize={16} color={'gray.700'}>
              {sid}
            </Text>
          </HStack>
        </VStack>
      </Flex>
      <Flex as={'div'} ref={mainTitle} direction={'column'} w={'100%'} align={'flex-start'} pt={6}>
        <Text fontSize={20} px={4} py={2} color={'black'}>
          詳細資料
        </Text>
        <BuildingRow building={building} />
        {mainData.map((data, idx) => {
          if (data.hidden) return null;

          const { key, title, isSubItem, collapseData, endWithBorder, noLeftPadding } = data;
          const { displayValue, unit } = getDisplayValueAndUnit(data, detailData);

          return (
            <ConditionalFragment key={key + idx} condition={isSet(displayValue)}>
              <MainDataItemDescription
                isSubItem={isSubItem}
                name={title}
                value={displayValue!}
                unit={unit}
                collapseData={collapseData}
                detailData={detailData}
                endWithBorder={endWithBorder}
                noLeftPadding={noLeftPadding}
              />
            </ConditionalFragment>
          );
        })}
        <Text px={4} color={'gray.500'} lineHeight={5} fontSize={'12px'} pt={4} pb={12}>
          本物件照片中之相關裝飾或其他物品，僅係提供買方做為參考之用，並不隨同該物件買賣或贈與。於簽訂相關買賣契約前，請你詳細確認附隨買賣之設備或家飾、贈品。
          <ConditionalFragment condition={detailData.country! === 'US'}>
            <br />
            <br />1 坪 = 35.5832 平方英呎。美國物件原面積單位為「平方英呎」，轉換成「坪」會有些許落差。
          </ConditionalFragment>
        </Text>
        <Flex w={'100%'} px={4}>
          <Stack w={'100%'} h={'1px'} bg={'gray.300'} />
        </Flex>
      </Flex>
      <ContentContainer>
        <ItemFeature
          //@ts-ignore
          tags={detailData.feature_tags ?? []}
          //@ts-ignore
          content={detailData.feature_content ?? ''}
          titleRef={featureTitle}
        />
      </ContentContainer>
      <div className={'pt-8'}>
        <ContentContainer py={3}>
          <Text as={'div'} ref={environmentTitle} fontSize={20} color={'black'}>
            周邊環境
          </Text>
        </ContentContainer>
        <MapViewSection
          sid={detailData.sid}
          //@ts-ignore
          items={trafficEnv}
          center={{
            lat: parseFloat(detailData.location_latitude ?? '0'),
            lng: parseFloat(detailData.location_longitude ?? '0'),
          }}
          country={detailData.country!}
        />
      </div>
      <ContentContainer>
        <HomeLoanCalculation country={detailData.country!} price={detailData.price ?? 0} className={'py-8'} />
      </ContentContainer>
      <AgentSection
        sid={detailData.sid}
        title={detailData.title}
        className={'px-4 pb-8'}
        contactFormPath={getContactFormPath(detailData.buildingId)}
        ctaText={getContactCTAText(detailData.buildingId)}
      />
      <ContactFooter sid={detailData.sid} />
      <SubNavBar
        mainTitle={mainTitle}
        videoTitle={videoTitle}
        featureTitle={featureTitle}
        environmentTitle={environmentTitle}
      />
    </FullPage>
  );
};

export const Caution: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <p className={classNames(className, 'text-xs text-gray500 font-medium')}>
      本物件照片中之相關裝飾或其他物品，僅係提供買方做為參考之用，並不隨同該物件買賣或贈與。於簽訂相關買賣契約前，請您詳細確認附隨買賣之設備或家飾、贈品。
    </p>
  );
};

const BuildingRow: FC<{ building: BuildingShowcaseDetailsEntity | null }> = ({ building }) => {
  if (!building) return null;

  return (
    <Stack w={'100%'} pr={4} pl={4} bg={'white'}>
      <Stack py={2} direction={'row'} w={'100%'} align={'flex-start'} justifyContent={'space-between'}>
        <div
          className={classNames(
            'flex items-center',
            'flex-1 flex-shrink-0',
            'min-w-[50%]',
            'text-gray900 leading-normal'
          )}
        >
          <Text>建案資訊</Text>
        </div>
        <Text as={'div'} color={'gray.900'} textAlign={'right'} className={'leading-normal'}>
          <Link href={`/building/${building.sid}`} passHref>
            <a className={'text-fb-blue underline'}>了解建案</a>
          </Link>
        </Text>
      </Stack>
    </Stack>
  );
};

const MainDataItemDescription = ({
  name,
  value,
  isSubItem,
  unit,
  collapseData,
  detailData,
  endWithBorder,
  noLeftPadding,
}: {
  name: string;
  value: string | number;
  isSubItem?: boolean;
  endWithBorder?: boolean;
  unit?: string | null;
  collapseData?: MainData['collapseData'];
  detailData: Props['detailData'];
  noLeftPadding?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const collapsibleRef = useRef<HTMLDivElement>(null);
  const isCollapsible = isSet(collapseData) && collapseData?.some((data) => !data.hidden);

  const height = useMemo(() => {
    if (isExpanded) return collapsibleRef.current?.scrollHeight || 0;
    else return 0;
  }, [isExpanded]);

  return (
    <div className={'w-full'}>
      <Stack w={'100%'} pr={4} pl={!noLeftPadding && isSubItem ? 8 : 4} bg={isSubItem ? 'gray.50' : 'white'}>
        <Stack
          py={2}
          direction={'row'}
          w={'100%'}
          align={'flex-start'}
          justifyContent={'space-between'}
          className={classNames({
            'border-b border-solid': endWithBorder,
          })}
          onClick={() => isCollapsible && setIsExpanded((prev) => !prev)}
        >
          <div
            className={classNames(
              'flex items-center',
              'flex-1 flex-shrink-0',
              'min-w-[50%]',
              'text-gray900 leading-normal'
            )}
          >
            <Text>{name}</Text>
            <ConditionalFragment condition={isCollapsible}>
              <div className={'ml-1'}>
                (<u>{isExpanded ? `隱藏細節` : `查看細節`}</u>)
              </div>
            </ConditionalFragment>
          </div>
          <Text as={'div'} color={'gray.900'} textAlign={'right'} className={'leading-normal'}>
            {`${value} ${parseFloat(value.toString()) ? unit : ''}`.split('\n').map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Text>
        </Stack>
      </Stack>
      <ConditionalFragment condition={isCollapsible}>
        <div
          className={'w-full'}
          style={{ height, transition: 'height', transitionDuration: '300ms' }}
          ref={collapsibleRef}
        >
          {collapseData?.map((data, idx) => {
            if (data.hidden) return null;

            const { key, isSubItem, title, endWithBorder: _endWithBorder } = data;
            const { displayValue, unit } = getDisplayValueAndUnit(data, detailData);

            return (
              <ConditionalFragment key={key + idx} condition={isSet(displayValue)}>
                <MainDataItemDescription
                  isSubItem={isSubItem}
                  name={title ?? ''}
                  value={displayValue!}
                  unit={unit}
                  detailData={detailData}
                  endWithBorder={_endWithBorder}
                />
              </ConditionalFragment>
            );
          })}
        </div>
      </ConditionalFragment>
    </div>
  );
};

// noinspection JSUnusedGlobalSymbols
export default HouseDetailContainer;
